import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import SupermarketTabs from "../components/SupermarketTabs";
import ProductSection from "../components/ProductSection";
import Footer from "../components/Footer";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { fetchActowizProducts, fetchActowizFeatures, fetchActowizTopDeals } from "../store/actowiz/actowizSlice";
import { apiRequest } from "../api/client";

function FemtyzHome({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const byPlatform = useAppSelector((s) => s.actowiz.byPlatform);
  const features = useAppSelector((s) => s.actowiz.features);
  const topDeals = useAppSelector((s) => s.actowiz.topDeals);
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const pageSize = 10;

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  useEffect(() => {
    dispatch(
      fetchActowizProducts({
        platform: "tesco",
        start: 0,
        offset: pageSize,
        mode: "replace",
      })
    );
    dispatch(
      fetchActowizProducts({
        platform: "morrison",
        start: 0,
        offset: pageSize,
        mode: "replace",
      })
    );
    dispatch(
      fetchActowizProducts({
        platform: "asda",
        start: 0,
        offset: pageSize,
        mode: "replace",
      })
    );
    dispatch(
      fetchActowizProducts({
        platform: "sainsbury",
        start: 0,
        offset: pageSize,
        mode: "replace",
      })
    );
    dispatch(fetchActowizFeatures({ start: 0, offset: pageSize, mode: "replace" }));
    dispatch(fetchActowizTopDeals({ start: 0, offset: pageSize, mode: "replace" }));
  }, [dispatch]);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  const heroDeal = useMemo(() => {
    const visibleDeals = (Array.isArray(topDeals) ? topDeals : []).slice(0, 5);
    if (!visibleDeals.length) return null;

    let bestDeal = null;

    visibleDeals.forEach((deal) => {
      const prices = (Array.isArray(deal?.comparison) ? deal.comparison : [])
        .map((row) => (typeof row?.price === "number" ? row.price : null))
        .filter((p) => typeof p === "number" && Number.isFinite(p));
      if (!prices.length) return;

      const cheapest = Math.min(...prices);
      const highest = Math.max(...prices);
      const savings = highest - cheapest;

      if (!bestDeal || cheapest < bestDeal.cheapest) {
        bestDeal = {
          cheapest,
          savings: savings > 0 ? savings : 0,
          title: deal?.title || deal?.sku || "Top deal item",
        };
      }
    });

    return bestDeal;
  }, [topDeals]);

  const handleAddToList = useCallback(async (product) => {
    const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
    if (!minimalRes.ok) {
      const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
      throw new Error(
        errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists."
      );
    }

    const lists = Array.isArray(minimalRes.data)
      ? minimalRes.data
      : Array.isArray(minimalRes.data?.data)
      ? minimalRes.data.data
      : [];

    if (!lists.length) {
      throw new Error("No grocery list found. Please create a list first.");
    }

    let selectedListId = null;
    if (lists.length === 1) {
      selectedListId =
        lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
    } else {
      const selected = await chooseList(lists);
      if (!selected) {
        const cancelError = new Error("ADD_TO_LIST_CANCELLED");
        cancelError.code = "ADD_TO_LIST_CANCELLED";
        throw cancelError;
      }
      selectedListId =
        selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
    }
    if (!selectedListId) {
      throw new Error("Unable to select grocery list.");
    }

    const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
    if (!productId) {
      throw new Error("Invalid product.");
    }

    const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
      method: "POST",
      body: {
        product_id: String(productId),
        image_url: product?.images?.[0] || "",
        price: product?.price ?? "",
        weight: product?.packsize || "",
        quantity: 1,
      },
    });

    if (!addRes.ok) {
      const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
      throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
    }
  }, [chooseList]);

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto flex max-w-8xl gap-6 px-4 py-6 md:px-6">
        <Sidebar />

        <section className="flex-1 space-y-8">
          {/* Hero / dashboard summary card */}
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="max-w-xl">
                <p className="text-xs font-semibold uppercase tracking-wide text-[#6E7B86]">
                  FEMTYZ Dashboard
                </p>
                <h1 className="mt-2 text-[28px] font-extrabold leading-tight text-[#24303A]">
                  Find the cheapest groceries faster
                </h1>
                <p className="mt-2 text-sm text-[#6E7B86]">
                  Compare prices across retailers, then add the best deals to your grocery list.
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/top-deals")}
                    className="rounded-full bg-[#7352C7] px-4 py-2 text-xs font-extrabold text-white shadow-sm hover:bg-[#6546c3]"
                  >
                    View Top Deals →
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/features")}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-extrabold text-[#7352C7] shadow-sm hover:bg-[#F5F7FB]"
                  >
                    Browse Featured
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/search")}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-extrabold text-[#7352C7] shadow-sm hover:bg-[#F5F7FB]"
                  >
                    Search
                  </button>
                </div>
              </div>

              <div className="rounded-3xl bg-[#7352C7] p-4 text-white shadow-sm w-full md:max-w-[320px]">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs font-semibold text-white/80">Best deal snapshot</div>
                    <div className="mt-2 text-3xl font-extrabold">
                      {heroDeal?.cheapest != null ? `£${heroDeal.cheapest.toFixed(2)}` : "—"}
                    </div>
                    {heroDeal?.title ? (
                      <div className="mt-1 text-[11px] font-medium text-white/90 line-clamp-2 max-w-[260px]">
                        {heroDeal.title}
                      </div>
                    ) : null}
                    <div className="mt-1 text-sm font-semibold text-white/90">
                      {heroDeal?.savings > 0
                        ? `Save £${heroDeal.savings.toFixed(2)}`
                        : "Compare across retailers"}
                    </div>
                  </div>
                </div>
                <div className="mt-3 rounded-2xl bg-white/10 px-3 py-2 text-xs text-white/90">
                  Tip: pick cheaper options to stretch your budget.
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
           


            <SupermarketTabs />
          </div>

          <ProductSection
            title="Today's Top Deals"
            sectionIdx={0}
            initialLimit={5}
            products={topDeals || []}
            onAddToList={handleAddToList}
            onViewMore={() => navigate("/top-deals")}
          />

          <ProductSection
            title="Features Products"
            sectionIdx={1}
            initialLimit={5}
            products={features || []}
            onAddToList={handleAddToList}
            onViewMore={() => navigate("/features")}
          />

          <ProductSection
            title="Tesco Products"
            sectionIdx={2}
            initialLimit={5}
            products={byPlatform?.tesco || []}
            onAddToList={handleAddToList}
            onViewMore={() => navigate("/platform/tesco")}
          />

          <ProductSection
            title="ASDA Products"
            sectionIdx={3}
            initialLimit={5}
            products={byPlatform?.asda || []}
            onAddToList={handleAddToList}
            onViewMore={() => navigate("/platform/asda")}
          />

          <ProductSection
            title="Morrisons Products"
            sectionIdx={4}
            initialLimit={5}
            products={byPlatform?.morrison || []}
            onAddToList={handleAddToList}
            onViewMore={() => navigate("/platform/morrison")}
          />

          <ProductSection
            title="Sainsbury's Products"
            sectionIdx={5}
            initialLimit={5}
            products={byPlatform?.sainsbury || []}
            onAddToList={handleAddToList}
            onViewMore={() => navigate("/platform/sainsbury")}
          />
        </section>
      </main>

      <Footer />

      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-xs text-slate-500">Select the list where you want to add this product.</p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-sm font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FemtyzHome;